import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BookOpen, Calendar, User, Phone, DollarSign, Plus, Save, 
  Download, Upload, Trash2, ChevronLeft, ChevronRight, Printer,
  FileText, X, Eye, AlertTriangle, Bell, Settings, History, CreditCard, Lock,
  ZoomIn, ZoomOut, RotateCcw, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '../utils/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Default balance alert threshold
const DEFAULT_BALANCE_THRESHOLD = 100000; // ₦100,000

const CustomerAccountRecords = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prices, setPrices] = useState({});
  const [frozenVariants, setFrozenVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [formData, setFormData] = useState({});
  const [balanceThreshold, setBalanceThreshold] = useState(() => {
    const saved = localStorage.getItem('balance_alert_threshold');
    return saved ? parseFloat(saved) : DEFAULT_BALANCE_THRESHOLD;
  });
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [customersWithHighBalance, setCustomersWithHighBalance] = useState([]);
  const [showHighBalanceList, setShowHighBalanceList] = useState(false);
  const [activeTab, setActiveTab] = useState('ledger');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const receiptInputRef1 = useRef(null);
  const receiptInputRef2 = useRef(null);
  const [viewingReceipt, setViewingReceipt] = useState(null); // For viewing receipts in modal
  const [zoomLevel, setZoomLevel] = useState(1); // Zoom level for image receipts
  
  const currencySymbol = localStorage.getItem('currency_symbol') || '₦';
  
  // Get current user role from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin';
  const isCustomer = currentUser?.role === 'customer';
  const [recalculating, setRecalculating] = useState(false);

  const handleBulkRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await axios.post(`${API}/customer-accounts/recalculate-all`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const { customers_processed, entries_updated } = res.data;
      toast.success(`Recalculated ${customers_processed} customer ledger(s) — ${entries_updated} entr${entries_updated === 1 ? 'y' : 'ies'} updated`);
      if (selectedCustomer) {
        fetchEntries();
        fetchSummary();
      }
      fetchCustomersWithHighBalance();
    } catch (e) {
      toast.error('Failed to recalculate ledgers');
    } finally {
      setRecalculating(false);
    }
  };
  
  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  useEffect(() => {
    if (isCustomer) {
      // For customers, fetch their own record by name
      fetchOwnCustomerRecord();
    } else {
      fetchCustomers();
      fetchCustomersWithHighBalance();
    }
    // Load frozen chicken pack variants for the frozen columns
    axios.get(`${API}/frozen-chicken/variants`, getAuthHeader())
      .then(res => setFrozenVariants(res.data || []))
      .catch(() => {});
  }, []);

  // For customer role: fetch their own record
  const fetchOwnCustomerRecord = async () => {
    try {
      const response = await axios.get(
        `${API}/customers/by-name/${encodeURIComponent(currentUser.name)}`,
        getAuthHeader()
      );
      const customer = response.data;
      setCustomers([customer]);
      setSelectedCustomer(customer);
    } catch (error) {
      console.error('Failed to fetch customer record');
    }
  };

  useEffect(() => {
    if (selectedCustomer) {
      fetchEntries();
      fetchSummary();
      fetchPrices();
      fetchPaymentHistory();
    }
  }, [selectedCustomer, currentYear, currentMonth]);

  // Check for balance alerts when summary changes
  useEffect(() => {
    if (summary && summary.balance > balanceThreshold) {
      toast.warning(
        `Alert: ${summary.customer_name}'s balance (${currencySymbol}${formatCurrency(summary.balance)}) exceeds the threshold of ${currencySymbol}${formatCurrency(balanceThreshold)}`,
        { duration: 5000 }
      );
    }
  }, [summary]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, getAuthHeader());
      setCustomers(response.data);
      // Deep-link support: pre-select a customer when ?customer=<id> is present
      // (e.g. clicking an affiliated customer in the Distributor Commission Ledger).
      const preId = new URLSearchParams(window.location.search).get('customer');
      if (preId) {
        const match = (response.data || []).find(c => c.id === preId);
        if (match) setSelectedCustomer(match);
      }
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchPaymentHistory = async () => {
    if (!selectedCustomer) return;
    try {
      setLoadingPayments(true);
      const response = await axios.get(
        `${API}/customer-accounts/${selectedCustomer.id}/payment-history`,
        getAuthHeader()
      );
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch payment history');
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchCustomersWithHighBalance = async () => {
    try {
      const response = await axios.get(
        `${API}/customer-accounts/high-balance?threshold=${balanceThreshold}`,
        getAuthHeader()
      );
      setCustomersWithHighBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch high balance customers');
    }
  };

  const handleThresholdChange = (newThreshold) => {
    setBalanceThreshold(newThreshold);
    localStorage.setItem('balance_alert_threshold', newThreshold.toString());
    setShowAlertSettings(false);
    toast.success(`Balance alert threshold updated to ${currencySymbol}${formatCurrency(newThreshold)}`);
    // Refresh high balance list
    setTimeout(fetchCustomersWithHighBalance, 500);
  };

  const fetchEntries = async () => {
    if (!selectedCustomer) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/customer-accounts/${selectedCustomer.id}/entries?year=${currentYear}&month=${currentMonth}`,
        getAuthHeader()
      );
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await axios.get(
        `${API}/customer-accounts/${selectedCustomer.id}/summary?year=${currentYear}&month=${currentMonth}`,
        getAuthHeader()
      );
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  const fetchPrices = async () => {
    if (!selectedCustomer) return;
    try {
      // Use the SAME "Current Active Prices" source as the Price Management page so the
      // displayed per-unit prices always match the prices actually used for bill calculation.
      const response = await axios.get(
        `${API}/price-schedules/current?customer_id=${selectedCustomer.id}`,
        getAuthHeader()
      );
      setPrices(response.data);
    } catch (error) {
      console.error('Failed to fetch prices');
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    setEditingDay(null);
    setFormData({});
  };

  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 1) {
        setCurrentMonth(12);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 12) {
        setCurrentMonth(1);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
    setEditingDay(null);
  };

  const getEntryForDay = (day) => {
    return entries.find(e => e.day === day) || null;
  };

  const startEditing = (day) => {
    // Only Admin can edit entries
    if (!isAdmin) {
      toast.error('Only Admin can edit ledger entries');
      return;
    }
    const existing = getEntryForDay(day);
    setEditingDay(day);
    const base = existing || {
      extra_large_crates: 0, extra_large_pieces: 0, extra_large_crack_crates: 0, extra_large_crack_pieces: 0,
      large_crates: 0, large_pieces: 0, large_crack_crates: 0, large_crack_pieces: 0,
      medium_crates: 0, medium_pieces: 0, medium_crack_crates: 0, medium_crack_pieces: 0,
      small_crates: 0, small_pieces: 0, small_crack_crates: 0, small_crack_pieces: 0,
      damaged_crates: 0, damaged_pieces: 0,
      empty_feed_bags_dozens: 0,
      payment: 0, pay_adjustment: 0, adjustment_reason: '',
      receipt_1: null, receipt_2: null
    };
    const frozenFlat = {};
    frozenVariants.forEach(v => { frozenFlat[`frozen_${v.key}`] = (base.frozen_chicken_packs?.[v.key] || 0); });
    setFormData({ ...base, ...frozenFlat });
  };

  const handleInputChange = (field, value) => {
    // Keep value as string to prevent cursor jumping
    // Conversion to number happens on save
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ponytail: fields are type=text so the mobile caret survives re-renders; digits filtered here
  const handleNumberChange = (field, value) => {
    const cleaned = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setFormData(prev => ({ ...prev, [field]: cleaned }));
  };

  // Signed decimal input (allows a leading minus) — used for balance Adjustments (refunds = negative)
  const handleSignedNumberChange = (field, value) => {
    let cleaned = value.replace(/[^0-9.-]/g, '');   // keep digits, dot, minus
    cleaned = cleaned.replace(/(?!^)-/g, '');        // minus only allowed at the start
    cleaned = cleaned.replace(/(\..*)\./g, '$1');    // only one decimal point
    setFormData(prev => ({ ...prev, [field]: cleaned }));
  };

  const handleReceiptUpload = async (e, receiptNum) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB allowed.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const receipt = {
        filename: file.name,
        content_type: file.type,
        data: reader.result.split(',')[1],
        size: file.size
      };
      setFormData(prev => ({
        ...prev,
        [`receipt_${receiptNum}`]: receipt
      }));
      toast.success(`Receipt ${receiptNum} uploaded`);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeReceipt = (receiptNum) => {
    setFormData(prev => ({
      ...prev,
      [`receipt_${receiptNum}`]: null
    }));
  };

  const saveEntry = async () => {
    if (!selectedCustomer || !editingDay) return;
    
    try {
      // Convert string values to numbers for the payload
      const payload = {
        customer_id: selectedCustomer.id,
        year: currentYear,
        month: currentMonth,
        day: editingDay,
        extra_large_crates: parseInt(formData.extra_large_crates) || 0,
        extra_large_pieces: parseInt(formData.extra_large_pieces) || 0,
        extra_large_crack_crates: parseInt(formData.extra_large_crack_crates) || 0,
        extra_large_crack_pieces: parseInt(formData.extra_large_crack_pieces) || 0,
        large_crates: parseInt(formData.large_crates) || 0,
        large_pieces: parseInt(formData.large_pieces) || 0,
        large_crack_crates: parseInt(formData.large_crack_crates) || 0,
        large_crack_pieces: parseInt(formData.large_crack_pieces) || 0,
        medium_crates: parseInt(formData.medium_crates) || 0,
        medium_pieces: parseInt(formData.medium_pieces) || 0,
        medium_crack_crates: parseInt(formData.medium_crack_crates) || 0,
        medium_crack_pieces: parseInt(formData.medium_crack_pieces) || 0,
        small_crates: parseInt(formData.small_crates) || 0,
        small_pieces: parseInt(formData.small_pieces) || 0,
        small_crack_crates: parseInt(formData.small_crack_crates) || 0,
        small_crack_pieces: parseInt(formData.small_crack_pieces) || 0,
        damaged_crates: parseInt(formData.damaged_crates) || 0,
        damaged_pieces: parseInt(formData.damaged_pieces) || 0,
        frozen_chicken_packs: frozenVariants.reduce((acc, v) => {
          const q = parseInt(formData[`frozen_${v.key}`]) || 0;
          if (q) acc[v.key] = q;
          return acc;
        }, {}),
        empty_feed_bags_dozens: parseInt(formData.empty_feed_bags_dozens) || 0,
        payment: parseFloat(formData.payment) || 0,
        pay_adjustment: parseFloat(formData.pay_adjustment) || 0,
        adjustment_reason: formData.adjustment_reason || '',
        receipt_1: formData.receipt_1,
        receipt_2: formData.receipt_2
      };
      
      await axios.post(`${API}/customer-accounts/entries`, payload, getAuthHeader());
      toast.success('Entry saved successfully');
      setEditingDay(null);
      fetchEntries();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const calculateBillPreview = () => {
    let bill = 0;
    // Use fallback to legacy crack prices if new fields are not set
    const crackCrate = (size) => prices[`${size}_crack_crate`] || prices.crack_crate || 0;
    const crackPiece = (size) => prices[`${size}_crack_piece`] || prices.crack_piece || 0;
    
    // Parse string values to numbers for calculation
    bill += (parseFloat(formData.extra_large_crates) || 0) * (prices.extra_large_crate || 0);
    bill += (parseFloat(formData.extra_large_pieces) || 0) * (prices.extra_large_piece || 0);
    bill += (parseFloat(formData.extra_large_crack_crates) || 0) * crackCrate('extra_large');
    bill += (parseFloat(formData.extra_large_crack_pieces) || 0) * crackPiece('extra_large');
    bill += (parseFloat(formData.large_crates) || 0) * (prices.large_crate || 0);
    bill += (parseFloat(formData.large_pieces) || 0) * (prices.large_piece || 0);
    bill += (parseFloat(formData.large_crack_crates) || 0) * crackCrate('large');
    bill += (parseFloat(formData.large_crack_pieces) || 0) * crackPiece('large');
    bill += (parseFloat(formData.medium_crates) || 0) * (prices.medium_crate || 0);
    bill += (parseFloat(formData.medium_pieces) || 0) * (prices.medium_piece || 0);
    bill += (parseFloat(formData.medium_crack_crates) || 0) * crackCrate('medium');
    bill += (parseFloat(formData.medium_crack_pieces) || 0) * crackPiece('medium');
    bill += (parseFloat(formData.small_crates) || 0) * (prices.small_crate || 0);
    bill += (parseFloat(formData.small_pieces) || 0) * (prices.small_piece || 0);
    bill += (parseFloat(formData.small_crack_crates) || 0) * crackCrate('small');
    bill += (parseFloat(formData.small_crack_pieces) || 0) * crackPiece('small');
    bill += (parseFloat(formData.damaged_crates) || 0) * (prices.damaged_crate || 0);
    bill += (parseFloat(formData.damaged_pieces) || 0) * (prices.damaged_piece || 0);
    // Frozen chicken packs
    const frozenPrices = prices.frozen_chicken_prices || {};
    frozenVariants.forEach(v => {
      bill += (parseFloat(formData[`frozen_${v.key}`]) || 0) * (frozenPrices[v.key] || 0);
    });
    // Empty chicken feed bags (per dozen)
    bill += (parseFloat(formData.empty_feed_bags_dozens) || 0) * (prices.empty_feed_bag_price || 0);
    return bill;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2 }).format(amount || 0);
  };

  const viewReceipt = (receipt) => {
    setZoomLevel(1); // Reset zoom when opening new receipt
    setViewingReceipt(receipt);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3)); // Max 3x zoom
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.25)); // Min 0.25x zoom
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const downloadReceipt = (receipt) => {
    const link = document.createElement('a');
    link.href = `data:${receipt.content_type};base64,${receipt.data}`;
    link.download = receipt.filename;
    link.click();
  };

  // Egg size configuration for the table
  // hasCracks: true means 4 columns (Crt, Pcs, CrkCrt, CrkPcs)
  // hasCracks: false means 2 columns (Crt, Pcs) - for Damaged only
  const eggSizes = [
    { key: 'extra_large', label: 'Extra Large', color: 'purple', hasCracks: true },
    { key: 'large', label: 'Large', color: 'emerald', hasCracks: true },
    { key: 'medium', label: 'Medium', color: 'blue', hasCracks: true },
    { key: 'small', label: 'Small', color: 'amber', hasCracks: true },
    { key: 'damaged', label: 'Damaged', color: 'orange', hasCracks: false }
  ];

  return (
    <div className="space-y-6" data-testid="customer-account-records-page">
      {/* Header with Balance Alert Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-emerald-600" />
            {isCustomer ? 'My Account Records' : 'Customer Account Records'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isCustomer ? 'View your egg purchases, payments, and account balance' : 'Track daily egg sales, payments, and account balances'}
          </p>
        </div>
        
        {/* Balance Alert Controls - Only for Admin/Manager */}
        {!isCustomer && (
          <div className="flex items-center gap-2">
            {/* High Balance Alert Button */}
            {customersWithHighBalance.length > 0 && (
              <Dialog open={showHighBalanceList} onOpenChange={setShowHighBalanceList}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="relative border-red-200 text-red-700 hover:bg-red-50">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Balance Alerts
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {customersWithHighBalance.length}
                    </span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Customers with High Balance
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-500 mb-4">
                    The following customers have outstanding balances exceeding {currencySymbol}{formatCurrency(balanceThreshold)}
                  </p>
                  <div className="space-y-2">
                    {customersWithHighBalance.map((cust, idx) => (
                      <div 
                        key={cust.customer_id}
                        className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                        onClick={() => {
                          const customer = customers.find(c => c.id === cust.customer_id);
                          if (customer) {
                            setSelectedCustomer(customer);
                            setShowHighBalanceList(false);
                          }
                        }}
                      >
                        <div>
                          <p className="font-semibold text-slate-800">{cust.customer_name}</p>
                          {cust.phone && <p className="text-sm text-slate-500">{cust.phone}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600 text-lg">
                            {currencySymbol}{formatCurrency(cust.balance)}
                          </p>
                          <p className="text-xs text-slate-500">Outstanding</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Alert Settings Button */}
            <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Alert Settings
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Balance Alert Settings
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Alert Threshold Amount</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Show alert when customer balance exceeds this amount
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">{currencySymbol}</span>
                    <Input
                      type="number"
                      value={balanceThreshold}
                      onChange={(e) => setBalanceThreshold(parseFloat(e.target.value) || 0)}
                      className="max-w-[200px]"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowAlertSettings(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleThresholdChange(balanceThreshold)}
                  >
                    Save Settings
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

            {/* Bulk Recalculate - Admin only */}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkRecalculate}
                disabled={recalculating}
                data-testid="bulk-recalculate-btn"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                title="Refresh every customer's ledger balance from the latest prices"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
                {recalculating ? 'Recalculating…' : 'Recalculate All'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Customer Selection - Hidden for customer role (auto-selected) */}
      {!isCustomer && (
        <div className="card p-4">
          <Label className="text-base font-semibold">Select Customer</Label>
          <Select onValueChange={handleCustomerChange} value={selectedCustomer?.id || ''}>
            <SelectTrigger className="mt-2 max-w-md h-16" data-testid="customer-select">
              {selectedCustomer ? (
                <span className="text-xl font-bold text-emerald-700">
                  {selectedCustomer.name}
                  {customersWithHighBalance.find(c => c.customer_id === selectedCustomer.id) && (
                    <span className="ml-2 text-red-500">⚠</span>
                  )}
                </span>
              ) : (
                <SelectValue placeholder="Choose a customer..." />
              )}
            </SelectTrigger>
            <SelectContent>
              {customers.map(customer => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                  {customersWithHighBalance.find(c => c.customer_id === customer.id) && (
                    <span className="ml-2 text-red-500">⚠</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selectedCustomer && (
        <>
          {/* Balance Alert Banner */}
          {summary && summary.balance > balanceThreshold && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-800">High Balance Alert</h4>
                <p className="text-red-700">
                  {selectedCustomer.name}'s outstanding balance of <strong>{currencySymbol}{formatCurrency(summary.balance)}</strong> exceeds 
                  the alert threshold of {currencySymbol}{formatCurrency(balanceThreshold)}.
                </p>
              </div>
            </div>
          )}

          {/* Summary Header Block */}
          <div className={`card p-6 ${summary && summary.balance > balanceThreshold ? 'bg-gradient-to-r from-red-900 to-red-800' : 'bg-gradient-to-r from-slate-900 to-slate-800'} text-white`}>
            <div className="flex flex-col lg:flex-row justify-between gap-6">
              {/* Left: Customer Info */}
              <div className="flex items-start gap-4">
                <div className={`w-16 h-16 ${summary && summary.balance > balanceThreshold ? 'bg-red-500/30' : 'bg-white/10'} rounded-xl flex items-center justify-center`}>
                  {summary && summary.balance > balanceThreshold ? (
                    <AlertTriangle className="w-8 h-8 text-red-300" />
                  ) : (
                    <User className="w-8 h-8 text-white/80" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{selectedCustomer.name}</h2>
                  {selectedCustomer.phone && (
                    <p className="text-white/70 flex items-center gap-2 mt-1">
                      <Phone className="w-4 h-4" />
                      {selectedCustomer.phone}
                    </p>
                  )}
                  {selectedCustomer.address && (
                    <p className="text-white/60 text-sm mt-1">{selectedCustomer.address}</p>
                  )}
                </div>
              </div>
              
              {/* Right: Account Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-xs text-white/60 uppercase">Balance B/F</p>
                  <p className={`text-lg font-bold ${(summary?.balance_bf || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {currencySymbol}{formatCurrency(Math.abs(summary?.balance_bf || 0))}
                    {(summary?.balance_bf || 0) > 0 && ' DR'}
                    {(summary?.balance_bf || 0) < 0 && ' CR'}
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-xs text-white/60 uppercase">Billed</p>
                  <p className="text-lg font-bold text-white">{currencySymbol}{formatCurrency(summary?.total_billed || 0)}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-xs text-white/60 uppercase">Tot Pmt</p>
                  <p className="text-lg font-bold text-emerald-400">{currencySymbol}{formatCurrency(summary?.total_payments || 0)}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-xs text-white/60 uppercase">Balance</p>
                  <p className={`text-lg font-bold ${(summary?.balance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {currencySymbol}{formatCurrency(Math.abs(summary?.balance || 0))}
                    {(summary?.balance || 0) > 0 && ' CR'}
                    {(summary?.balance || 0) < 0 && ' DR'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs for Ledger and Payment History */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="ledger" className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Monthly Ledger
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Payment History
                {paymentHistory?.payments?.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    {paymentHistory.payments.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Monthly Ledger Tab */}
            <TabsContent value="ledger" className="space-y-4">
              {/* Month Navigation */}
              <div className="card p-4">
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => handleMonthChange('prev')}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <h3 className="text-lg font-semibold text-slate-800">
                      {monthNames[currentMonth - 1]} {currentYear}
                    </h3>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleMonthChange('next')}>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* Current Prices Reference */}
              <div className="card p-4 bg-slate-50">
                <h4 className="font-semibold text-slate-700 mb-1 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Current Prices (Per Unit)
                </h4>
                {prices.effective_date && (
                  <p className="text-xs text-slate-500 mb-3" data-testid="current-prices-effective-since">
                    Effective since {formatDate(prices.effective_date)}
                    {prices.end_date && <span className="ml-1 text-amber-600">(until {formatDate(prices.end_date)})</span>}
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-sm">
                  <div className="bg-white p-2 rounded border">
                    <p className="text-purple-600 font-medium">Extra Large</p>
                    <p className="text-xs text-slate-500">Crate: {currencySymbol}{formatCurrency(prices.extra_large_crate)}</p>
                    <p className="text-xs text-slate-500">Piece: {currencySymbol}{formatCurrency(prices.extra_large_piece)}</p>
                    <p className="text-xs text-red-500 mt-1">Crk: {currencySymbol}{formatCurrency(prices.extra_large_crack_crate || prices.crack_crate || 0)}/{currencySymbol}{formatCurrency(prices.extra_large_crack_piece || prices.crack_piece || 0)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-emerald-600 font-medium">Large</p>
                    <p className="text-xs text-slate-500">Crate: {currencySymbol}{formatCurrency(prices.large_crate)}</p>
                    <p className="text-xs text-slate-500">Piece: {currencySymbol}{formatCurrency(prices.large_piece)}</p>
                    <p className="text-xs text-red-500 mt-1">Crk: {currencySymbol}{formatCurrency(prices.large_crack_crate || prices.crack_crate || 0)}/{currencySymbol}{formatCurrency(prices.large_crack_piece || prices.crack_piece || 0)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-blue-600 font-medium">Medium</p>
                    <p className="text-xs text-slate-500">Crate: {currencySymbol}{formatCurrency(prices.medium_crate)}</p>
                    <p className="text-xs text-slate-500">Piece: {currencySymbol}{formatCurrency(prices.medium_piece)}</p>
                    <p className="text-xs text-red-500 mt-1">Crk: {currencySymbol}{formatCurrency(prices.medium_crack_crate || prices.crack_crate || 0)}/{currencySymbol}{formatCurrency(prices.medium_crack_piece || prices.crack_piece || 0)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-amber-600 font-medium">Small</p>
                    <p className="text-xs text-slate-500">Crate: {currencySymbol}{formatCurrency(prices.small_crate)}</p>
                    <p className="text-xs text-slate-500">Piece: {currencySymbol}{formatCurrency(prices.small_piece)}</p>
                    <p className="text-xs text-red-500 mt-1">Crk: {currencySymbol}{formatCurrency(prices.small_crack_crate || prices.crack_crate || 0)}/{currencySymbol}{formatCurrency(prices.small_crack_piece || prices.crack_piece || 0)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-orange-600 font-medium">Damaged</p>
                    <p className="text-xs text-slate-500">Crate: {currencySymbol}{formatCurrency(prices.damaged_crate)}</p>
                    <p className="text-xs text-slate-500">Piece: {currencySymbol}{formatCurrency(prices.damaged_piece)}</p>
                  </div>
                </div>
              </div>

              {/* Info Banner - Quantities from Order Pickups */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
                <div className="text-blue-500 mt-0.5">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">Egg quantities are populated from Customer Order Pick-up</p>
                  <p className="text-xs text-blue-600 mt-1">
                    To update egg quantities, record a new pickup in the "Customer Order Pick-up" tab. 
                    {isAdmin ? ' As Admin, you can also directly edit entries for corrections.' : ' Only Admin can directly edit ledger entries.'}
                  </p>
                </div>
              </div>

              {/* Daily Ledger Table */}
              <div className="card overflow-hidden">
                {/* Mobile scroll hint */}
                <div className="md:hidden text-xs text-slate-500 p-2 bg-slate-50 flex items-center gap-1 border-b">
                  <span>👉</span> Swipe left/right to see all columns
                </div>
                <div className="overflow-x-auto overscroll-x-contain [touch-action:pan-x_pan-y]" style={{ WebkitOverflowScrolling: 'touch' }}>
                <table className="w-full text-sm min-w-[1600px]" data-testid="ledger-table">
                  <thead>
                    <tr className="bg-slate-100">
                      <th rowSpan="2" className="p-2 border text-center font-semibold sticky left-0 bg-slate-100 z-10">Day</th>
                      {eggSizes.map(size => (
                        <th key={size.key} colSpan={size.hasCracks ? 4 : 2} className={`p-2 border text-center font-semibold bg-${size.color}-100 text-${size.color}-800`}>
                          {size.label}
                        </th>
                      ))}
                      {frozenVariants.map(v => (
                        <th key={v.key} rowSpan="2" className="p-2 border text-center font-semibold bg-cyan-100 text-cyan-800 min-w-[56px]">
                          {v.label || `${v.key}`}
                        </th>
                      ))}
                      <th rowSpan="2" className="p-2 border text-center font-semibold bg-amber-100 text-amber-800 min-w-[60px]">Empty Bags (dz)</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold bg-slate-200">Bill</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold bg-emerald-100 text-emerald-800">Payment</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold bg-amber-100 text-amber-800">Adjust</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold">Reason</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold">Receipt 1</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold">Receipt 2</th>
                  <th rowSpan="2" className="p-2 border text-center font-semibold sticky right-0 bg-slate-100 z-10 min-w-[80px]">Actions</th>
                </tr>
                <tr className="bg-slate-50">
                  {eggSizes.map(size => (
                    size.hasCracks ? (
                      <React.Fragment key={size.key}>
                        <th className="p-1 border text-xs">Crt</th>
                        <th className="p-1 border text-xs">Pcs</th>
                        <th className="p-1 border text-xs bg-red-50 text-red-700">CrkCrt</th>
                        <th className="p-1 border text-xs bg-red-50 text-red-700">CrkPcs</th>
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={size.key}>
                        <th className="p-1 border text-xs">Crt</th>
                        <th className="p-1 border text-xs">Pcs</th>
                      </React.Fragment>
                    )
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: getDaysInMonth(currentYear, currentMonth) }, (_, i) => i + 1).map(day => {
                  const entry = getEntryForDay(day);
                  const isEditing = editingDay === day;
                  
                  return (
                    <tr key={day} data-testid={`ledger-row-${day}`} className={`${isEditing ? 'bg-yellow-50' : 'hover:bg-slate-50'} ${entry ? '' : 'text-slate-400'}`}>
                      <td className="p-2 border text-center font-medium sticky left-0 bg-white z-10">{day}</td>
                      
                      {/* Extra Large */}
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.extra_large_crates || ''} onChange={(e) => handleNumberChange('extra_large_crates', e.target.value)} /> : entry?.extra_large_crates || '-'}</td>
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.extra_large_pieces || ''} onChange={(e) => handleNumberChange('extra_large_pieces', e.target.value)} /> : entry?.extra_large_pieces || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.extra_large_crack_crates || ''} onChange={(e) => handleNumberChange('extra_large_crack_crates', e.target.value)} /> : entry?.extra_large_crack_crates || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.extra_large_crack_pieces || ''} onChange={(e) => handleNumberChange('extra_large_crack_pieces', e.target.value)} /> : entry?.extra_large_crack_pieces || '-'}</td>
                      
                      {/* Large */}
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.large_crates || ''} onChange={(e) => handleNumberChange('large_crates', e.target.value)} /> : entry?.large_crates || '-'}</td>
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.large_pieces || ''} onChange={(e) => handleNumberChange('large_pieces', e.target.value)} /> : entry?.large_pieces || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.large_crack_crates || ''} onChange={(e) => handleNumberChange('large_crack_crates', e.target.value)} /> : entry?.large_crack_crates || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.large_crack_pieces || ''} onChange={(e) => handleNumberChange('large_crack_pieces', e.target.value)} /> : entry?.large_crack_pieces || '-'}</td>
                      
                      {/* Medium */}
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.medium_crates || ''} onChange={(e) => handleNumberChange('medium_crates', e.target.value)} /> : entry?.medium_crates || '-'}</td>
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.medium_pieces || ''} onChange={(e) => handleNumberChange('medium_pieces', e.target.value)} /> : entry?.medium_pieces || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.medium_crack_crates || ''} onChange={(e) => handleNumberChange('medium_crack_crates', e.target.value)} /> : entry?.medium_crack_crates || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.medium_crack_pieces || ''} onChange={(e) => handleNumberChange('medium_crack_pieces', e.target.value)} /> : entry?.medium_crack_pieces || '-'}</td>
                      
                      {/* Small */}
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.small_crates || ''} onChange={(e) => handleNumberChange('small_crates', e.target.value)} /> : entry?.small_crates || '-'}</td>
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.small_pieces || ''} onChange={(e) => handleNumberChange('small_pieces', e.target.value)} /> : entry?.small_pieces || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.small_crack_crates || ''} onChange={(e) => handleNumberChange('small_crack_crates', e.target.value)} /> : entry?.small_crack_crates || '-'}</td>
                      <td className="p-1 border text-center bg-red-50">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.small_crack_pieces || ''} onChange={(e) => handleNumberChange('small_crack_pieces', e.target.value)} /> : entry?.small_crack_pieces || '-'}</td>
                      
                      {/* Damaged */}
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.damaged_crates || ''} onChange={(e) => handleNumberChange('damaged_crates', e.target.value)} /> : entry?.damaged_crates || '-'}</td>
                      <td className="p-1 border text-center">{isEditing ? <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.damaged_pieces || ''} onChange={(e) => handleNumberChange('damaged_pieces', e.target.value)} /> : entry?.damaged_pieces || '-'}</td>
                      
                      {/* Frozen Chicken packs */}
                      {frozenVariants.map(v => (
                        <td key={v.key} className="p-1 border text-center bg-cyan-50/40">
                          {isEditing ? (
                            <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData[`frozen_${v.key}`] || ''} onChange={(e) => handleNumberChange(`frozen_${v.key}`, e.target.value)} data-testid={`input-frozen-${v.key}-day`} />
                          ) : (entry?.frozen_chicken_packs?.[v.key] || '-')}
                        </td>
                      ))}
                      
                      {/* Empty Chicken Feed Bags (dozens) */}
                      <td className="p-1 border text-center bg-amber-50/40">
                        {isEditing ? (
                          <Input type="text" inputMode="numeric" pattern="[0-9]*" className="w-12 h-7 text-xs p-1" value={formData.empty_feed_bags_dozens || ''} onChange={(e) => handleNumberChange('empty_feed_bags_dozens', e.target.value)} data-testid="input-empty-feed-bags-day" />
                        ) : (entry?.empty_feed_bags_dozens || '-')}
                      </td>
                      
                      {/* Bill */}
                      <td className="p-1 border text-center font-medium bg-slate-50">
                        {isEditing ? (
                          <span className="text-emerald-600" data-testid="bill-preview">{currencySymbol}{formatCurrency(calculateBillPreview())}</span>
                        ) : (
                          entry?.bill_amount ? `${currencySymbol}${formatCurrency(entry.bill_amount)}` : '-'
                        )}
                      </td>
                      
                      {/* Payment */}
                      <td className="p-1 border text-center">
                        {isEditing ? (
                          <Input type="text" inputMode="decimal" pattern="[0-9.]*" className="w-20 h-7 text-xs p-1" value={formData.payment || ''} onChange={(e) => handleNumberChange('payment', e.target.value)} data-testid="input-payment" />
                        ) : (
                          entry?.payment ? (
                            <span className="text-emerald-600 inline-flex items-center gap-1" title={entry.payment_source === 'bank_reconciliation' && !entry.payment_manual_override ? 'Auto-recorded from Bank Reconciliation' : (entry.payment_manual_override ? 'Manually entered/corrected' : '')}>
                              {currencySymbol}{formatCurrency(entry.payment)}
                              {entry.payment_source === 'bank_reconciliation' && !entry.payment_manual_override && (
                                <span className="text-[9px] px-1 rounded bg-sky-100 text-sky-600" data-testid="bank-synced-badge">bank</span>
                              )}
                            </span>
                          ) : '-'
                        )}
                      </td>
                      
                      {/* Adjustment */}
                      <td className="p-1 border text-center">
                        {isEditing ? (
                          <Input type="text" inputMode="text" pattern="-?[0-9.]*" className="w-16 h-7 text-xs p-1" value={formData.pay_adjustment ?? ''} onChange={(e) => handleSignedNumberChange('pay_adjustment', e.target.value)} placeholder="+/-" data-testid="input-pay-adjustment" />
                        ) : (
                          entry?.pay_adjustment ? (
                            <span className={entry.pay_adjustment > 0 ? 'text-emerald-600' : 'text-red-600'}>
                              {entry.pay_adjustment > 0 ? '+' : ''}{currencySymbol}{formatCurrency(entry.pay_adjustment)}
                            </span>
                          ) : '-'
                        )}
                      </td>
                      
                      {/* Reason */}
                      <td className="p-1 border text-center">
                        {isEditing ? (
                          <Input type="text" className="w-24 h-7 text-xs p-1" value={formData.adjustment_reason || ''} onChange={(e) => handleInputChange('adjustment_reason', e.target.value)} placeholder="Reason..." />
                        ) : (
                          <span className="text-xs text-slate-500">{entry?.adjustment_reason || '-'}</span>
                        )}
                      </td>
                      
                      {/* Receipt 1 */}
                      <td className="p-1 border text-center">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input type="file" ref={receiptInputRef1} onChange={(e) => handleReceiptUpload(e, 1)} className="hidden" accept="image/*,.pdf" />
                            {formData.receipt_1 ? (
                              <div className="flex items-center gap-1">
                                <FileText className="w-3 h-3 text-emerald-600" />
                                <button onClick={() => removeReceipt(1)} className="text-red-500 hover:text-red-700">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => receiptInputRef1.current?.click()}>
                                <Upload className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          entry?.receipt_1 ? (
                            <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => viewReceipt(entry.receipt_1)} title="View Receipt 1">
                              <Eye className="w-3 h-3 text-emerald-600" />
                            </Button>
                          ) : '-'
                        )}
                      </td>
                      
                      {/* Receipt 2 */}
                      <td className="p-1 border text-center">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input type="file" ref={receiptInputRef2} onChange={(e) => handleReceiptUpload(e, 2)} className="hidden" accept="image/*,.pdf" />
                            {formData.receipt_2 ? (
                              <div className="flex items-center gap-1">
                                <FileText className="w-3 h-3 text-emerald-600" />
                                <button onClick={() => removeReceipt(2)} className="text-red-500 hover:text-red-700">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => receiptInputRef2.current?.click()}>
                                <Upload className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          entry?.receipt_2 ? (
                            <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => viewReceipt(entry.receipt_2)} title="View Receipt 2">
                              <Eye className="w-3 h-3 text-emerald-600" />
                            </Button>
                          ) : '-'
                        )}
                      </td>
                      
                      {/* Actions */}
                      <td className="p-1 border text-center sticky right-0 bg-white z-10 min-w-[80px]">
                        {isEditing ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Button size="sm" className="h-6 px-2 bg-emerald-600 hover:bg-emerald-700" onClick={saveEntry} data-testid="save-entry-btn">
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setEditingDay(null)} data-testid="cancel-edit-btn">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : isAdmin ? (
                          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => startEditing(day)} title="Edit entry (Admin only)" data-testid={`edit-day-${day}`}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        ) : (
                          <span className="text-slate-400" title="Only Admin can edit">
                            <Lock className="w-3 h-3" />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot data-testid="ledger-footer">
                  {(() => {
                    const billTotal = entries.reduce((s, e) => s + (e.bill_amount || 0), 0);
                    const payTotal = entries.reduce((s, e) => s + (e.payment || 0), 0);
                    const adjTotal = entries.reduce((s, e) => s + (e.pay_adjustment || 0), 0);
                    const bf = summary?.balance_bf || 0;
                    const closing = summary?.balance || 0;
                    return (
                      <>
                        <tr className="bg-slate-100 font-semibold">
                          <td colSpan={20 + frozenVariants.length} className="p-2 border text-right text-slate-700">Month Totals</td>
                          <td className="p-2 border text-center text-slate-800">{currencySymbol}{formatCurrency(billTotal)}</td>
                          <td className="p-2 border text-center text-emerald-700">{currencySymbol}{formatCurrency(payTotal)}</td>
                          <td className="p-2 border text-center text-amber-700">{currencySymbol}{formatCurrency(adjTotal)}</td>
                          <td colSpan={4} className="border"></td>
                        </tr>
                        <tr className="bg-slate-50">
                          <td colSpan={20 + frozenVariants.length} className="p-2 border text-right text-slate-500 text-xs">
                            Balance B/F: <span className={bf > 0 ? 'text-red-600 font-semibold' : bf < 0 ? 'text-emerald-600 font-semibold' : ''}>
                              {currencySymbol}{formatCurrency(Math.abs(bf))}{bf > 0 ? ' DR' : bf < 0 ? ' CR' : ''}
                            </span>
                          </td>
                          <td colSpan={7} className="border"></td>
                        </tr>
                        <tr className="bg-slate-900 text-white font-bold" data-testid="closing-balance-row">
                          <td colSpan={20 + frozenVariants.length} className="p-3 border text-right uppercase tracking-wide">Closing Balance C/F</td>
                          <td colSpan={7} className={`p-3 border text-center text-base ${closing >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="closing-balance-value">
                            {currencySymbol}{formatCurrency(Math.abs(closing))}
                            {closing > 0 ? ' CR' : closing < 0 ? ' DR' : ''}
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </tfoot>
            </table>
                </div>
              </div>
            </TabsContent>

            {/* Payment History Tab */}
            <TabsContent value="payments" className="space-y-4">
              {/* Payment Summary Cards */}
              {paymentHistory && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="card p-4 bg-emerald-50 border-emerald-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <CreditCard className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-emerald-600">Total Payments</p>
                        <p className="text-xl font-bold text-emerald-700">
                          {currencySymbol}{formatCurrency(paymentHistory.total_payments || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4 bg-amber-50 border-amber-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-amber-600">Total Adjustments</p>
                        <p className={`text-xl font-bold ${(paymentHistory.total_adjustments || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {(paymentHistory.total_adjustments || 0) >= 0 ? '+' : ''}{currencySymbol}{formatCurrency(paymentHistory.total_adjustments || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4 bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <History className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-600">Grand Total Paid</p>
                        <p className="text-xl font-bold text-blue-700">
                          {currencySymbol}{formatCurrency(paymentHistory.grand_total || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment History Table */}
              <div className="card">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <History className="w-5 h-5" />
                    All Payment Records
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Complete history of all payments and adjustments
                  </p>
                </div>

                {loadingPayments ? (
                  <div className="p-8 text-center text-slate-500">Loading payment history...</div>
                ) : !paymentHistory?.payments?.length ? (
                  <div className="p-12 text-center">
                    <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-slate-700">No Payment Records</h4>
                    <p className="text-slate-500 mt-2">No payments have been recorded for this customer yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="payment-history-table">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="p-3 text-left font-semibold text-slate-700">Date</th>
                          <th className="p-3 text-right font-semibold text-slate-700">Bill Amount</th>
                          <th className="p-3 text-right font-semibold text-slate-700">Payment</th>
                          <th className="p-3 text-right font-semibold text-slate-700">Adjustment</th>
                          <th className="p-3 text-left font-semibold text-slate-700">Reason</th>
                          <th className="p-3 text-right font-semibold text-slate-700">Total Paid</th>
                          <th className="p-3 text-right font-semibold text-slate-700">Running Balance</th>
                          <th className="p-3 text-center font-semibold text-slate-700">Receipts</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {paymentHistory.payments.map((record, idx) => (
                          <tr key={record.id || idx} className="hover:bg-slate-50">
                            <td className="p-3">
                              <span className="font-medium text-slate-800">
                                {new Date(record.date).toLocaleDateString('en-NG', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            </td>
                            <td className="p-3 text-right text-slate-600">
                              {currencySymbol}{formatCurrency(record.bill_amount)}
                            </td>
                            <td className="p-3 text-right">
                              <span className="text-emerald-600 font-medium">
                                {currencySymbol}{formatCurrency(record.payment)}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              {record.pay_adjustment !== 0 ? (
                                <span className={record.pay_adjustment > 0 ? 'text-emerald-600' : 'text-red-600'}>
                                  {record.pay_adjustment > 0 ? '+' : ''}{currencySymbol}{formatCurrency(record.pay_adjustment)}
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="p-3 text-slate-600 max-w-[200px] truncate">
                              {record.adjustment_reason || '-'}
                            </td>
                            <td className="p-3 text-right">
                              <span className="font-semibold text-emerald-700">
                                {currencySymbol}{formatCurrency(record.total_paid)}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <span className={`font-semibold ${record.running_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {currencySymbol}{formatCurrency(Math.abs(record.running_balance))}
                                {record.running_balance > 0 ? ' DR' : record.running_balance < 0 ? ' CR' : ''}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {record.receipt_1 && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2"
                                    onClick={() => viewReceipt(record.receipt_1)}
                                    title="View Receipt 1"
                                  >
                                    <Eye className="w-3 h-3 text-emerald-600" />
                                  </Button>
                                )}
                                {record.receipt_2 && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2"
                                    onClick={() => viewReceipt(record.receipt_2)}
                                    title="View Receipt 2"
                                  >
                                    <Eye className="w-3 h-3 text-blue-600" />
                                  </Button>
                                )}
                                {!record.receipt_1 && !record.receipt_2 && (
                                  <span className="text-slate-400">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Receipt Viewer Modal */}
      <Dialog open={!!viewingReceipt} onOpenChange={() => { setViewingReceipt(null); setZoomLevel(1); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {viewingReceipt?.filename || 'Receipt'}
              </span>
              <div className="flex items-center gap-2 mr-8">
                {/* Zoom controls - only show for images */}
                {viewingReceipt?.content_type?.startsWith('image/') && (
                  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.25}
                      className="h-8 w-8 p-0"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium w-14 text-center">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 3}
                      className="h-8 w-8 p-0"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetZoom}
                      className="h-8 w-8 p-0"
                      title="Reset Zoom"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadReceipt(viewingReceipt)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex-1 overflow-auto bg-slate-100 rounded-lg p-4 min-h-[400px]">
            <div className="flex items-center justify-center min-h-full">
              {viewingReceipt && (
                viewingReceipt.content_type?.startsWith('image/') ? (
                  <div 
                    className="transition-transform duration-200 ease-out"
                    style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center center' }}
                  >
                    <img 
                      src={`data:${viewingReceipt.content_type};base64,${viewingReceipt.data}`}
                      alt={viewingReceipt.filename}
                      className="max-w-full object-contain rounded shadow-lg cursor-zoom-in"
                      style={{ maxHeight: zoomLevel === 1 ? '70vh' : 'none' }}
                      onClick={handleZoomIn}
                      draggable={false}
                    />
                  </div>
                ) : viewingReceipt.content_type === 'application/pdf' ? (
                  <iframe
                    src={`data:${viewingReceipt.content_type};base64,${viewingReceipt.data}`}
                    title={viewingReceipt.filename}
                    className="w-full h-[70vh] rounded"
                  />
                ) : (
                  <div className="text-center text-slate-500">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                    <p className="font-medium">{viewingReceipt.filename}</p>
                    <p className="text-sm mt-2">Preview not available for this file type</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => downloadReceipt(viewingReceipt)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download to view
                    </Button>
                  </div>
                )
              )}
            </div>
          </div>
          {/* Zoom hint for images */}
          {viewingReceipt?.content_type?.startsWith('image/') && (
            <div className="text-center text-xs text-slate-400 mt-2">
              Click image to zoom in • Use controls above to adjust • Scroll to pan when zoomed
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerAccountRecords;
